import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiInfo, FiX, FiCompass, FiCheckCircle, FiAlertTriangle } from "react-icons/fi";
import { getPageGuide } from "../data/pageGuides";

/**
 * Floating "page guide" button for dashboard pages, explaining the purpose,
 * functionality and workflow of whichever page is open.
 *
 * Mounted once globally alongside AskDivy. It resolves its content from the
 * current route and renders nothing on routes without a guide, so individual
 * pages need no changes.
 *
 * Sits directly above the Ask Dythi bubble: the bubble column is anchored at
 * bottom-6 and stands roughly 88px tall (64px orb + gap + label), so 8rem
 * clears it with a comfortable gap. The trigger stays below the chat's
 * z-[1000] so an open chat panel covers it, while the modal sits above both.
 */

const THEMES = {
  member: {
    accent: "#F29A0C",
    accentDark: "#C8660A",
    ring: "rgba(242,154,12,0.35)",
    bloom: "rgba(242,154,12,0.5)",
    glow: "rgba(242,154,12,0.12)",
    surface: "linear-gradient(145deg, rgba(255,255,255,0.97), rgba(254,249,240,0.98))",
    header: "linear-gradient(135deg,#F29A0C,#E97818)",
    softBg: "rgba(242,154,12,0.08)",
    softBorder: "rgba(242,154,12,0.22)",
  },
  admin: {
    accent: "#6B8A5A",
    accentDark: "#4E6841",
    ring: "rgba(107,138,90,0.35)",
    bloom: "rgba(107,138,90,0.5)",
    glow: "rgba(107,138,90,0.14)",
    surface: "linear-gradient(145deg, rgba(255,255,255,0.97), rgba(246,249,243,0.98))",
    header: "linear-gradient(135deg,#6B8A5A,#4E6841)",
    softBg: "rgba(107,138,90,0.08)",
    softBorder: "rgba(107,138,90,0.22)",
  },
};

function Section({ icon: Icon, label, accent, children }) {
  return (
    <div className="mb-5 last:mb-0">
      <p
        className="flex items-center gap-2 text-[10.5px] font-black uppercase tracking-wider mb-2.5"
        style={{ color: accent }}
      >
        <Icon size={13} />
        {label}
      </p>
      {children}
    </div>
  );
}

export default function PageInfoGuide() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [labelShown, setLabelShown] = useState(false);

  const guide = getPageGuide(pathname);

  // Close the panel whenever the route changes, so it never carries the
  // previous page's content onto a new one.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;

    const handleKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handleKey);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!guide) return null;

  const theme = THEMES[guide.audience] || THEMES.member;

  return (
    <>
      {/* ── Trigger, stacked comfortably above the Ask Dythi bubble ─────────────────── */}
      <div className="fixed bottom-[12.5rem] right-3.5 md:bottom-[8.75rem] md:right-6 z-[96] w-14 md:w-20 flex flex-col items-center pointer-events-none">
        <div className="relative flex items-center justify-center">
          {/* Label is revealed on hover/focus only, so the corner stays calm. */}
          <AnimatePresence>
            {labelShown && (
              <motion.span
                initial={{ opacity: 0, x: 8, scale: 0.96 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 8, scale: 0.96 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                className="absolute right-full mr-2.5 px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap bg-white pointer-events-none"
                style={{
                  color: theme.accentDark,
                  border: `1px solid ${theme.softBorder}`,
                  boxShadow: `0 6px 18px rgba(0,0,0,0.10), 0 0 14px ${theme.glow}`,
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                About this page
              </motion.span>
            )}
          </AnimatePresence>

          <motion.button
            type="button"
            onClick={() => setOpen(true)}
            onMouseEnter={() => setLabelShown(true)}
            onMouseLeave={() => setLabelShown(false)}
            onFocus={() => setLabelShown(true)}
            onBlur={() => setLabelShown(false)}
            initial={{ opacity: 0, scale: 0.7, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.93 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            aria-label={`About this page: ${guide.title}`}
            className="pointer-events-auto w-9 h-9 md:w-12 md:h-12 rounded-full flex items-center justify-center cursor-pointer outline-none bg-white"
            style={{
              color: theme.accent,
              border: `1.5px solid ${theme.ring}`,
              boxShadow: `0 8px 22px rgba(0,0,0,0.10), 0 0 0 3px ${theme.glow}, 0 0 16px 3px ${theme.bloom}`,
            }}
          >
            <FiInfo className="text-sm md:text-lg" />
          </motion.button>
        </div>
      </div>

      {/* ── Modal ───────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 sm:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setOpen(false)}
              className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
            />

            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label={`${guide.title} page guide`}
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 10 }}
              transition={{ duration: 0.3, ease: [0.25, 1, 0.5, 1] }}
              className="relative w-full max-w-2xl max-h-[86vh] flex flex-col rounded-[28px] overflow-hidden"
              style={{
                background: theme.surface,
                border: `1px solid ${theme.softBorder}`,
                boxShadow: `0 30px 80px ${theme.glow}, 0 10px 30px rgba(0,0,0,0.18)`,
              }}
            >
              {/* Header */}
              <div
                className="flex items-start gap-3 px-6 py-5 shrink-0"
                style={{ background: theme.header }}
              >
                <div className="w-10 h-10 rounded-[14px] bg-white/20 border border-white/30 flex items-center justify-center text-white shrink-0">
                  <FiInfo size={19} />
                </div>

                <div className="flex-1 min-w-0 pt-0.5">
                  <p className="text-[9.5px] font-black uppercase tracking-[0.12em] text-white/70">
                    About this page
                  </p>
                  <h2
                    className="text-[19px] sm:text-[21px] font-black text-white leading-tight tracking-tight"
                    style={{ fontFamily: "Poppins, sans-serif" }}
                  >
                    {guide.title}
                  </h2>
                  {guide.subtitle && (
                    <p className="text-[12px] font-medium text-white/80 mt-0.5">
                      {guide.subtitle}
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close page guide"
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white/75 hover:text-white hover:bg-white/15 transition-colors cursor-pointer shrink-0"
                >
                  <FiX size={16} />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto px-6 py-5">
                {guide.purpose && (
                  <Section icon={FiCompass} label="Purpose" accent={theme.accentDark}>
                    <p className="text-[13.5px] leading-[22px] text-zinc-700 font-medium">
                      {guide.purpose}
                    </p>
                  </Section>
                )}

                {guide.capabilities?.length > 0 && (
                  <Section
                    icon={FiCheckCircle}
                    label="What you can do here"
                    accent={theme.accentDark}
                  >
                    <div className="space-y-2">
                      {guide.capabilities.map((item, i) => (
                        <div key={i} className="flex items-start gap-2.5">
                          <FiCheckCircle
                            size={14}
                            className="shrink-0 mt-[3px] text-emerald-600"
                          />
                          <span className="text-[13px] leading-[21px] font-semibold text-zinc-800">
                            {item}
                          </span>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                {guide.workflow?.length > 0 && (
                  <Section icon={FiCompass} label="How it works" accent={theme.accentDark}>
                    <ol className="space-y-2.5">
                      {guide.workflow.map((step, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <span
                            className="shrink-0 w-[22px] h-[22px] rounded-full flex items-center justify-center text-[11px] font-black text-white"
                            style={{ background: theme.accent }}
                          >
                            {i + 1}
                          </span>
                          <span className="text-[13px] leading-[21px] font-medium text-zinc-700 pt-0.5">
                            {step}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </Section>
                )}

                {guide.notes?.length > 0 && (
                  <Section
                    icon={FiAlertTriangle}
                    label="Good to know"
                    accent={theme.accentDark}
                  >
                    <div
                      className="rounded-[18px] p-3.5 space-y-2"
                      style={{
                        background: theme.softBg,
                        border: `1px solid ${theme.softBorder}`,
                      }}
                    >
                      {guide.notes.map((note, i) => (
                        <div key={i} className="flex items-start gap-2.5">
                          <span
                            className="shrink-0 mt-[7px] w-1.5 h-1.5 rounded-full"
                            style={{ background: theme.accent }}
                          />
                          <span className="text-[12.5px] leading-[20px] font-medium text-zinc-700">
                            {note}
                          </span>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}
              </div>

              {/* Footer */}
              <div className="shrink-0 px-6 py-3.5 bg-white/70 border-t border-black/5 flex items-center justify-between gap-3">
                <p className="text-[11px] font-semibold text-zinc-500">
                  Need more help? Ask Dythi, the assistant below.
                </p>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="h-9 px-5 rounded-full text-[12.5px] font-extrabold text-white cursor-pointer hover:opacity-95 transition-opacity shrink-0"
                  style={{ background: theme.header }}
                >
                  Got it
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
